import { useEffect, useState } from 'react';
import Callback from '../../Services/Callback';
import { useSocketListener } from '../../Components/Socket';
import syncManager, { PHASE } from './feeds';

/**
 * The server pushes one business-scoped nudge — `sync` — whenever anything a feed
 * reads changes ({@see Sockets/Domain/TopicSubscriptions/SyncSubscription}), and
 * this runs the ordinary delta when it arrives. The event is a trigger, never a
 * source: it carries no rows and no ids, which is what makes one topic for
 * everybody safe and a dropped event a cost in latency rather than data.
 */

const SYNC_TOPIC = 'sync';

/**
 * The settle is not only about collapsing a burst: the push is emitted from the
 * event bus, inside the writing transaction, so a sync starting the instant it
 * arrived could read the row before it committed and persist an `asOf` past it.
 * This plus `Chunker::DELTA_OVERLAP_SECONDS` is comfortably more than a commit.
 */
const SETTLE_MS = 1_500;
const MIN_GAP_MS = 5_000;

/** Trailing-edge: one sync per burst, and the last event in a burst is never lost. */
const createSyncNudge = ({ sync, isBusy }) => {
    let timer = null;
    let lastSyncAt = 0;

    const nudge = () => {
        if (timer) {
            return;
        }

        timer = setTimeout(() => {
            timer = null;
            lastSyncAt = Date.now();

            const joined = isBusy();
            const running = sync();

            // A run already in flight froze its `asOf` before this change landed,
            // so go again once it settles rather than waiting out the interval.
            // A declined sync (offline, signed out) returns nothing and ends here.
            if (joined && running) {
                Promise.resolve(running).finally(nudge);
            }
        }, Math.max(SETTLE_MS, lastSyncAt + MIN_GAP_MS - Date.now()));
    };

    return nudge;
};

// A run in flight, and nothing else: PHASE.ERROR persists after a failed run, so
// counting it as busy turns every push into a self-sustaining 5-second retry loop.
const RUNNING_PHASES = [PHASE.BOOTSTRAPPING, PHASE.SYNCING];

const nudgeSync = createSyncNudge({
    sync: () => syncManager.sync(),
    isBusy: () => RUNNING_PHASES.includes(syncManager.state.phase),
});

/**
 * For the dev tool — the only place a push is visible at all, since a working one
 * looks exactly like a well-timed interval.
 */
const pushes = { count: 0, lastAt: null };
const onPush = new Callback();

const recordPush = () => {
    pushes.count += 1;
    pushes.lastAt = new Date().toISOString();
    onPush.invoke({ ...pushes });
};

const useRealtimePushes = () => {
    const [state, setState] = useState(() => ({ ...pushes }));

    useEffect(() => onPush.listen(setState), []);

    return state;
};

/**
 * Read off the sync singleton rather than the offline context, which every button
 * in the app consumes.
 */
const useLocalDataActive = () => {
    const [active, setActive] = useState(() => !!syncManager.state.requested);

    useEffect(() => syncManager.onChange.listen((state) => setActive(!!state.requested)), []);

    return active;
};

/** Mounted once, under the socket provider. */
const SyncSubscription = () => {
    const active = useLocalDataActive();

    useSocketListener(active ? { topic: SYNC_TOPIC } : null, () => {
        recordPush();
        nudgeSync();
    });

    return null;
};

export default SyncSubscription;
export { SyncSubscription, SYNC_TOPIC, createSyncNudge, useRealtimePushes, SETTLE_MS, MIN_GAP_MS };
